import Add from '@mui/icons-material/Add';
import CalendarToday from '@mui/icons-material/CalendarToday';
import LocalCarWash from '@mui/icons-material/LocalCarWash';
import LocationOn from '@mui/icons-material/LocationOn';
import cx from 'classnames';
import { ReactNode } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from '../app/hooks';
import BookingSummaryBar from '../components/BookingSummaryBar';
import useBookingPricingConfig from '../hooks/useBookingPricingConfig';
import SkinSwitchLink from '../consumer/SkinSwitchLink';
import { isPlanBooking } from '../util/bookingPricing';
import Tab from '../components/Tab';
import { PageMain } from '../components/SkipLink';
import TabContainer from '../components/TabContainer';
import styles from '../sass/components/Booking.module.scss';
import { useTenant } from '../components/TenantContext';
import { normalizeThemePreset } from '../util/themePreset';
import {
  ROUTE_BOOK_ADD_ONS,
  ROUTE_BOOK_LOCATION,
  ROUTE_BOOK_ROOT,
  ROUTE_BOOK_SCHEDULE,
  ROUTE_BOOK_SERVICES,
  ROUTE_ROOT,
} from '../util/constants';

/**
 * One booking step's link. A step not reached yet is not a link at all -
 * it used to be a live, focusable link that only `pointer-events: none`
 * kept a mouse off - but a disabled one (APG: no href, aria-disabled), so
 * it is announced and skipped by Tab. `name` is the whole "Step 2: Select
 * Services": on a phone only the icon of the other steps is on screen.
 */
function StepLink({
  to,
  available,
  current,
  name,
  children,
}: {
  to: string;
  available: boolean;
  current: boolean;
  name: string;
  children: ReactNode;
}) {
  if (!available) {
    return (
      // eslint-disable-next-line jsx-a11y/interactive-supports-focus
      <span
        role="link"
        aria-disabled="true"
        aria-label={name}
        className={cx(styles.link, styles.disabledLink)}
      >
        {children}
      </span>
    );
  }
  return (
    <Link
      className={styles.link}
      to={to}
      aria-label={name}
      aria-current={current ? 'step' : undefined}
    >
      {children}
    </Link>
  );
}

export default function Booking() {
  const { t } = useTranslation();
  const location = useLocation().pathname;
  // Class hook for a tenant theme preset's stylesheet: it restyles the steps
  // strip as one of the site's cream bands. False for every tenant on the
  // default preset, so their class list is unchanged.
  const themed = normalizeThemePreset(useTenant()?.themePreset) !== 'default';
  const currentUser = useAppSelector((state) => state.auth.currentUser);

  // Keep the prepay percentage in sync with the server so every step shows
  // the same number the booking will be created with.
  useBookingPricingConfig();
  const {
    serviceAddress,
    inService,
  } = useAppSelector((s) => s.serviceLocation);
  // An address the service-area check said we do not serve opens no later
  // step (RequireLocation sends a typed URL back too).
  const addressUsable = !!serviceAddress && inService !== false;

  const hasSelectedServices = useAppSelector((state) => (
    state.serviceTypes.services.length
  ));
  // Plan bookings skip the add-ons step, so the tab bar shows three steps.
  const planBooking = useAppSelector((state) => (
    isPlanBooking(state.serviceTypes.services)
  ));
  // Aperio leads with the plan, which has no add-ons step: the strip shows
  // "Select Add Ons" only once a one-time service is actually in the basket.
  // Every other tenant keeps the step until a plan is picked, as before.
  // Under the preset the strip keeps a fixed three steps: optional add-ons
  // are part of step 2, so the count never jumps from 3 to 4 when a one-time
  // service goes in the basket (QA 2026-09-25 #49).
  const hideAddOnsStep = planBooking || themed;
  // Add-ons belong to step 2 when their own tab is hidden.
  const onStepTwo = location === ROUTE_BOOK_SERVICES
    || (themed && location === ROUTE_BOOK_ADD_ONS);
  const tabClass = hideAddOnsStep
    ? 'container__col-xs-4'
    : 'container__col-xs-3';
  const scheduleStep = t('booking.steps.step', {
    n: hideAddOnsStep ? 3 : 4,
  });

  const staffStepOne = currentUser.roles?.includes('manager')
    || currentUser.roles?.includes('technician')
    || currentUser.roles?.includes('tester')
    || currentUser.roles?.includes('sales');
  const onStepOne = location === ROUTE_ROOT || location === ROUTE_BOOK_LOCATION;
  const stepOneLabel = staffStepOne
    ? ': Customer + Location' : t('booking.steps.location');

  return (
    <PageMain className={cx(styles.main, { 'apr-booking': themed })}>
      {/* "Booking one car? Use simple booking." (Aperio's consumer skin,
          consumer/SkinSwitchLink): renders nothing for every other tenant. */}
      <SkinSwitchLink />
      {/* An ordered list of steps, one link each, the current one marked
          aria-current="step" and the ones not reached yet aria-disabled
          (QA 2026-09-25 #29). Same classes as before, so the strip looks
          the same for every tenant. */}
      <TabContainer steps label={t('booking.steps.label')}>
        <Tab
          className={tabClass}
          step
          selected={
            location === ROUTE_BOOK_ROOT
            || location === ROUTE_BOOK_LOCATION
            || location === ROUTE_BOOK_SERVICES
            || location === ROUTE_BOOK_ADD_ONS
            || location === ROUTE_BOOK_SCHEDULE
          }
        >
          <StepLink
            to={ROUTE_BOOK_LOCATION}
            available
            current={onStepOne}
            name={`${t('booking.steps.step', { n: 1 })}${stepOneLabel}`}
          >
            <LocationOn />
            <div className={cx(
              styles.tabText, {
                [styles.selectedTab]: onStepOne,
              },
            )}
            >
              <span
                className={cx(
                  {
                    [styles.selected]: onStepOne,
                  },
                  styles.includeMobile,
                )}
              >
                {t('booking.steps.step', { n: 1 })}
              </span>
              <span className={styles.doNotIncludeMobile}>
                {stepOneLabel}
              </span>
            </div>
          </StepLink>
        </Tab>
        <Tab
          className={tabClass}
          step
          disabled={!addressUsable}
          selected={
            location === ROUTE_BOOK_SERVICES
            || location === ROUTE_BOOK_ADD_ONS
            || location === ROUTE_BOOK_SCHEDULE
          }
        >
          <StepLink
            to={ROUTE_BOOK_SERVICES}
            available={addressUsable}
            current={onStepTwo}
            name={`${t('booking.steps.step', { n: 2 })}${
              t('booking.steps.services')}`}
          >
            <LocalCarWash />
            <div className={cx(
              styles.tabText, {
                [styles.selectedTab]: onStepTwo,
              },
            )}
            >
              {' '}
              <span
                className={cx(
                  {
                    [styles.selected]: onStepTwo,
                  },
                  styles.includeMobile,
                )}
              >
                {t('booking.steps.step', { n: 2 })}
              </span>
              <span className={styles.doNotIncludeMobile}>
                {t('booking.steps.services')}
              </span>
            </div>
          </StepLink>
        </Tab>
        {!hideAddOnsStep && (
        <Tab
          className={tabClass}
          step
          disabled={!hasSelectedServices || !addressUsable}
          selected={
            location === ROUTE_BOOK_ADD_ONS
            || location === ROUTE_BOOK_SCHEDULE
          }
        >
          <StepLink
            to={ROUTE_BOOK_ADD_ONS}
            available={!!hasSelectedServices && addressUsable}
            current={location === ROUTE_BOOK_ADD_ONS}
            name={`${t('booking.steps.step', { n: 3 })}${
              t('booking.steps.addOns')}`}
          >
            <Add />
            <div className={cx(
              styles.tabText, {
                [styles.selectedTab]: location === ROUTE_BOOK_ADD_ONS,
              },
            )}
            >
              <span
                className={cx(
                  {
                    [styles.selected]: (location === ROUTE_BOOK_ADD_ONS),
                  },
                  styles.includeMobile,
                )}
              >
                {t('booking.steps.step', { n: 3 })}
              </span>
              <span className={styles.doNotIncludeMobile}>
                {t('booking.steps.addOns')}
              </span>
            </div>
          </StepLink>
        </Tab>
        )}
        <Tab
          className={tabClass}
          step
          disabled={!hasSelectedServices || !addressUsable}
          selected={location === ROUTE_BOOK_SCHEDULE}
        >
          <StepLink
            to={ROUTE_BOOK_SCHEDULE}
            available={!!hasSelectedServices && addressUsable}
            current={location === ROUTE_BOOK_SCHEDULE}
            name={`${scheduleStep}${t('booking.steps.schedule')}`}
          >
            <CalendarToday />
            <div className={cx(
              styles.tabText, {
                [styles.selectedTab]: location === ROUTE_BOOK_SCHEDULE,
              },
            )}
            >
              <span
                className={cx(
                  { [styles.selected]: location === ROUTE_BOOK_SCHEDULE },
                  styles.includeMobile,
                )}
              >
                {scheduleStep}
              </span>
              <span className={styles.doNotIncludeMobile}>
                {t('booking.steps.schedule')}
              </span>
            </div>
          </StepLink>
        </Tab>
      </TabContainer>
      {/* Under the preset the summary rides in each step's bottom bar
          (placement="footer"), so it is not pinned here as well. */}
      {!themed && <BookingSummaryBar />}
      <Outlet />
    </PageMain>
  );
}
